// ----------------------------------------------------------------------
// <copyright file="Izenda.RelationshipStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * Izenda Relationship View Model
 */
!(function() {
  var m = require('mobx');
  var dataModelDataFetcher = require('fetchers/Izenda.DataModelDataFetcher');
  var reportDesignerFetcher = require('fetchers/Izenda.ReportDesignerDataFetcher');
  var Paging = require('share/components/controls/DataGrid/helpers/Izenda.PagingModel');
  var RelationshipDetail = require('./models/Izenda.RelationshipDetail');
  var BUNDLE = require('./data/Izenda.RelationshipConstants').BUNDLE;
  var objectState = require('logic/data/Izenda.ObjectStateConstants');
  var constants = require('logic/data/Izenda.ReportDesignerConstants');
  var uiState = require('logic/Izenda.UiState');
  var CommonMessages = require('share/messages/Izenda.CommonMessages');

  const { find, filter, isEmpty, some, clone, map, assign, placeholder, flatten, omitBy, isNil, maxBy, findIndex } = require('lodash/fp');

  function RelationshipVM(querySource) {
    var colSortDirs = {
      PositionId: 'ASC'
    };
    this.querySource = querySource;
    this.querySourceId = !!querySource ? querySource.id : null;
    this.dataObjectName = !!querySource ? querySource.name : '';
    this.databaseId = !!querySource ? querySource.connectionId : null;
    this.editable = true;
    this.modifiedRelationships = [];

    m.extendObservable(this, {
      joinTypes: [],
      databases: [],
      relationshipList: [],
      isLoading: true,
      pageInfo: new Paging(),
      alertMessage: CommonMessages.EMPTY_MESSAGE,
      showInvalidScreen: false,
      showInvalidMessage: false,
      hasChange: function() {
        if (this.showInvalidScreen) {
          return true;
        }

        return some(['isDirty', true], this.relationshipList);
      },
      defaultSort: function() {
        return this.relationshipList.length > 0 ? colSortDirs : {};
      }
    });
  }

  RelationshipVM.prototype.initData = function(isPopup) {
    this.disposeObserveTenantID = m.observe(
      uiState,
      'tenantId',
      function () {
        if (uiState.silentUpdate) return;
        this.setShowInvalidScreen(false);
        this.goToPage(1);
      }.bind(this)
    );

    if (this.showInvalidScreen) {
      this.modifiedRelationships = [];
      this.syncModifiedRelationships();
    }

    this.resetData();
    !isPopup &&
      uiState.setCheckDirtyFunction(
        function() {
          return this.hasChange;
        }.bind(this)
      );

    return !this.showInvalidScreen
      ? Promise.join(this.getRelationships(), this.getJoinTypes())
      : Promise.join(this.getInvalidRelationships(), this.getJoinTypes());
  };

  RelationshipVM.prototype.destroy = function(isPopup) {
    !isPopup && uiState.resetCheckDirtyFunction();
    this.setShowInvalidScreen(false);
    this.disposeObserveTenantID();
  };

  /**
   * Get init data (list of stored procedure)
   */
  RelationshipVM.prototype.resetData = function() {
    this.relationshipList = [];
    this.isLoading = true;
    var DefaultSortCollection = BUNDLE.SORTDEFAULTLIST;
    var colSortDir = clone(DefaultSortCollection.DEFAULT_SORT);
    this.pageInfo = new Paging(colSortDir, BUNDLE.DEFAULT_CRITERIA);
  };

  /**
   * get relationship List from db
   */
  RelationshipVM.prototype.getRelationships = function() {
    var pageInfo = {
      pageSize: this.pageInfo.pageSize,
      pageIndex: this.pageInfo.pageIndex
    };

    // TODO for sort
    this.pageInfo.buildPagingRequest();
    var sortOrders = this.pageInfo.sortOrders;

    return Promise.join(
      dataModelDataFetcher.getRelationships(
        this.querySourceId,
        uiState.getTenantId(),
        pageInfo,
        sortOrders,
        this.pageInfo.searchCriteria
      ),
      dataModelDataFetcher.getBasicConnectionsInfo(uiState.getTenantId()),
      // populate data only after get list of database name for that tenant/system setting
      this.populateData.bind(this)
    );
  };

  /**
   * get modified relationships
   */
  RelationshipVM.prototype.getModifiedRelationships = function() {
    var updateList = filter(function(o) {
      return o.state != objectState.NONE;
    }, this.relationshipList);

    // create list of Relationship
    var newData = map(function(item) {
      return {
        id: item.id,
        joinConnectionId: item.joinConnectionId,
        foreignConnectionId: item.foreignConnectionId,
        joinQuerySourceId: item.joinQuerySourceId,
        foreignQuerySourceId: item.foreignQuerySourceId,
        joinFieldId: item.joinFieldId,
        foreignFieldId: item.foreignFieldId,
        alias: item.alias,
        systemRelationship: item.systemRelationship,
        joinType: item.joinType,
        modified: item.modified,
        positionId: item.positionId,
        position: parseInt(item.position),
        state: item.state,
        disabled: item.disabled
      };
    }, updateList);

    return newData;
  };

  /**
   * Sync modified relationship in invalid screen
   */
  RelationshipVM.prototype.syncModifiedRelationships = function() {
    const newData = this.getModifiedRelationships();
    if (!isEmpty(newData)) {
      // Store all modified relationship for Invalid Screen sort/search
      if (isEmpty(this.modifiedRelationships)) {
        this.modifiedRelationships = [...this.modifiedRelationships, ...newData];
      } else {
        map(item => {
          if (!some(['position', item.position], this.modifiedRelationships)) {
            this.modifiedRelationships.push(item);
          } else {
            // Replace data for the relationship on original array
            const indexInModified = this.getIndexOfModified(item);
            this.modifiedRelationships[indexInModified] = assign(this.modifiedRelationships[indexInModified], item);
          }
        }, newData);
      }
    }
  };

  /**
   * get invalid relationship List from db
   */
  RelationshipVM.prototype.getInvalidRelationships = function() {
    this.syncModifiedRelationships();
    this.pageInfo.buildPagingRequest();
    var sortOrders = this.pageInfo.sortOrders;

    return Promise.join(
      dataModelDataFetcher.getInvalidRelationships(
        this.querySourceId,
        uiState.getTenantId(),
        sortOrders,
        this.pageInfo.searchCriteria,
        this.modifiedRelationships
      ),
      dataModelDataFetcher.getBasicConnectionsInfo(uiState.getTenantId()),
      // populate data only after get list of database name for that tenant/system setting
      this.populateData.bind(this)
    );
  };

  /**
   * Create list of query source details
   * @param {array} spDetails
   */
  RelationshipVM.prototype.populateData = function(relationships, databaseList) {
    this.databases = databaseList || [];
    const editable = this.editable;
    const showInvalidScreen = this.showInvalidScreen;

    relationships = relationships || {};
    var relationshipList = relationships.result;

    // create list of Relationship
    var newData = map(querySource => {
      const newRelationship = new RelationshipDetail(querySource);
      newRelationship.setEditable(editable);
      if (showInvalidScreen) {
        const indexInModified = this.getIndexOfModified(newRelationship);
        indexInModified !== -1 && (this.modifiedRelationships[indexInModified].invalid = querySource.invalid);
        newRelationship.markDirty();
      }
      return newRelationship;
    }, relationshipList);

    // update to model
    this.relationshipList = newData;

    // update page info
    var pageInfo = this.pageInfo;
    pageInfo.setTotalRecords(relationships.total);
    return null;
  };

  /**
   * Create list of invalid errors
   * @param {array} fieldCheck
   */
  RelationshipVM.prototype.getInvalidErrors = function(fieldCheck) {
    const allError = map(function(item) {
      const errors = m.toJS(item.errors);
      if (!isEmpty(errors)) {
        const relationshipErrors = find({ key: 'Relationships' }, errors);
        if (relationshipErrors) {
          map(function(field) {
            if (field == 'positionId') return;
            errors.push({
              key: `${relationshipErrors.key}[${item.position}]${field}`,
              messages: relationshipErrors.messages
            });
          }, fieldCheck);
        }

        return errors;
      }
    }, this.relationshipList);

    return omitBy(isNil, flatten(allError));
  };

  RelationshipVM.prototype.getCriteriaList = function() {
    var criteriaCollection = !!this.querySource ? BUNDLE.DATASOURCE_CRITERIA_LIST : BUNDLE.DEFAULT_CRITERIA_LIST;
    return criteriaCollection;
  };

  /**
   * Go to page
   * @param {number} pageIndex
   * @returns
   */
  RelationshipVM.prototype.goToPage = function(pageIndex) {
    this.pageInfo.setPageIndex(pageIndex);
    this.getRelationships();
  };

  /**
   * Get data for Join Type dropdown list
   */
  RelationshipVM.prototype.getJoinTypes = function() {
    var assignJoinTypes = function(result) {
      result = result || {};
      // create list of join type
      this.joinTypes = map(function(item) {
        return {
          value: item,
          key: item
        };
      }, result);
    }.bind(this);

    return dataModelDataFetcher.getJoinTypes().then(assignJoinTypes);
  };

  RelationshipVM.prototype.validateModifiedRelationships = function() {
    // Validate on invalid relationship screen
    if (this.showInvalidScreen) {
      this.syncModifiedRelationships();
      return dataModelDataFetcher.validateRelationships(this.modifiedRelationships);
    }

    // Validate on normal relationship screen
    const newData = this.getModifiedRelationships();
    if (newData.length > 0) {
      return dataModelDataFetcher.validateRelationships(newData);
    } else {
      return Promise.resolve({ success: true });
    }
  };

  /**
   * Validate all relationships
   */
  RelationshipVM.prototype.validate = function() {
    var isValid = true;

    m.transaction(
      function() {
        this.relationshipList.forEach(function(relationship) {
          isValid = relationship.validate() && isValid;
        });
      }.bind(this)
    );

    return isValid;
  };

  /**
   * Show error message for each relationship
   */
  RelationshipVM.prototype.processErrorAfterSave = function(result) {
    var relationshipErrors = result.relationshipErrors;
    var requestError = result.messages;

    // reset error to ensure only show new error
    var list = map(function(item) {
      item.generalEror = '';
      return item;
    }, this.relationshipList);

    if (!isEmpty(relationshipErrors)) {
      m.transaction(function() {
        placeholder(relationshipErrors).forEach(function(err) {
          var item = $izenda.grep(list, function(value) {
            return value.position === err.id;
          });

          if (!isEmpty(item)) {
            item[0].generalEror = err.message;
          }
        });
      });
    }
    if (!isEmpty(requestError)) {
      return requestError;
    }
  };

  /**
   * save all relationships
   */
  RelationshipVM.prototype.save = function() {
    const newData = this.getModifiedRelationships();

    if (newData.length > 0) {
      return dataModelDataFetcher.saveRelationships(newData);
    } else {
      return Promise.resolve({ success: true });
    }
  };

  /**
   * delete selected relationship item
   */
  RelationshipVM.prototype.handleDeleteAction = function(relationship) {
    const handleOnEmptyRelationship = relationships => {
      if (!some(item => !isNil(item), relationships) && isEmpty(this.modifiedRelationships)) {
        this.setShowInvalidScreen(false);
        this.resetData();
        return this.getRelationships();
      }
      return Promise.resolve(relationships);
    };

    const removeRelationship = relationship => {
      const indexInModified = this.getIndexOfModified(relationship);
      this.relationshipList.splice(this.relationshipList.indexOf(relationship), 1);
      this.modifiedRelationships.splice(indexInModified, 1);
    };

    if (!isEmpty(relationship.id)) {
      const handleAfterDeleteRelationship = result => {
        if (result.success) {
          if (this.showInvalidScreen) {
            removeRelationship(relationship);
            return this.getInvalidRelationships().then(() => {
              return handleOnEmptyRelationship(this.relationshipList);
            });
          }
          return this.getRelationships();
        }
        return null;
      };

      return dataModelDataFetcher.deleteRelationship(relationship.id).then(handleAfterDeleteRelationship);
    } else {
      removeRelationship(relationship);
      if (this.showInvalidScreen) {
        return this.getInvalidRelationships().then(() => {
          return handleOnEmptyRelationship(this.relationshipList);
        });
      } else {
        this.pageInfo.descreaseRecord();
      }
    }
    return Promise.resolve();
  };

  /**
   * get new position
   */
  RelationshipVM.prototype.getNewPosition = function() {
    const maxPosition = maxBy(item => parseInt(item.position), this.relationshipList);
    return !isNil(maxPosition) && maxPosition.position ? parseInt(maxPosition.position) + 1 : null;
  };

  /**
   * add new relationship
   */
  RelationshipVM.prototype.addNew = function() {
    /**
     * "id": "id",
     * "joinConnectionId": "join connection id",
     * "foreignConnectionId": "foreign connection id",
     * "joinQuerySourceId": "join query source id",
     * "foreignQuerySourceId": "foreign query source id",
     * "joinFieldId": "join field id",
     * "foreignFieldId": "foreign field id",
     * "alias": "alias",
     * "systemRelationship": true/false,
     * "joinType": "join type",
     * "position": "empty string"
     */

    var item = {
      id: null,
      joinConnectionId: this.databaseId,
      foreignConnectionId: null,
      joinQuerySourceId: this.querySourceId,
      foreignQuerySourceId: null,
      joinFieldId: null,
      foreignFieldId: null,
      alias: '',
      systemRelationship: false,
      joinType: 'Inner',
      position: this.getNewPosition()
    };

    var relationship = new RelationshipDetail(item);
    this.relationshipList.unshift(relationship);
    relationship.markDirty();
    this.pageInfo.increaseRecord();

    return Promise.resolve();
  };

  /**
   * Copy selected relationships
   */
  RelationshipVM.prototype.copy = function(source) {
    const item = {
      id: null,
      joinConnectionId: source.joinConnectionId,
      foreignConnectionId: source.foreignConnectionId,
      joinQuerySourceId: source.joinQuerySourceId,
      joinQuerySourceName: source.joinQuerySourceName,
      foreignQuerySourceId: source.foreignQuerySourceId,
      foreignQuerySourceName: source.foreignQuerySourceName,
      joinFieldId: source.joinFieldId,
      foreignFieldId: source.foreignFieldId,
      alias: source.alias,
      systemRelationship: false,
      joinType: source.joinType,
      position: this.getNewPosition()
    };

    const indexOfRelationship = this.relationshipList.indexOf(source) + 1;
    const relationship = new RelationshipDetail(item);
    relationship.markDirty();
    this.relationshipList.splice(indexOfRelationship, 0, relationship);
    this.pageInfo.increaseRecord();

    return Promise.resolve();
  };

  /**
   * Reset new value to default
   * Relationship which is not allow to add new value
   * from dropdown list
   * {Params} value, label, isNew (flag)
   */
  RelationshipVM.prototype.resetNewValueToDefault = function(value, label, isNew) {
    return isNew ? { value: '', label: '', isNew: isNew } : { value: value, label: label, isNew: isNew };
  };

  /**
   * set alert message
   * @param {String} type
   * @param {String} message
   */
  RelationshipVM.prototype.setAlertMessage = function(type, message) {
    this.alertMessage = {
      type: type,
      message: message
    };
  };

  /**
   * Check join type between two entities in one relationship
   * @param {object} currentRow
   */
  RelationshipVM.prototype.checkExistEntities = function(currentRow) {
    var newJoinType = currentRow.joinType;
    if (!currentRow.joinQuerySourceId || !currentRow.foreignQuerySourceId) {
      return null;
    }

    return find(function(dataSource) {
      if (currentRow === dataSource) {
        return false;
      }
      return (
        (dataSource.joinQuerySourceId === currentRow.joinQuerySourceId &&
          dataSource.foreignQuerySourceId === currentRow.foreignQuerySourceId &&
          dataSource.joinType !== newJoinType) ||
        (dataSource.joinQuerySourceId === currentRow.foreignQuerySourceId &&
          dataSource.foreignQuerySourceId === currentRow.joinQuerySourceId &&
          ((dataSource.joinType !== newJoinType &&
            (dataSource.joinType !== constants.JOIN_TYPE.LEFT || newJoinType !== constants.JOIN_TYPE.RIGHT) &&
            (dataSource.joinType !== constants.JOIN_TYPE.RIGHT || newJoinType !== constants.JOIN_TYPE.LEFT)) ||
            (dataSource.joinType === constants.JOIN_TYPE.LEFT && newJoinType === constants.JOIN_TYPE.LEFT) ||
            (dataSource.joinType === constants.JOIN_TYPE.RIGHT && newJoinType === constants.JOIN_TYPE.RIGHT)))
      );
    }, this.relationshipList);
  };

  /**
   * Update join type between two entities in one relationship
   * @param {type} currentRow
   */
  RelationshipVM.prototype.updateJoinTypeForSameEntities = m.action(function(currentRow) {
    if (!currentRow.joinQuerySourceId || !currentRow.foreignQuerySourceId) {
      return null;
    }

    // clear Field/JoinField when joinType is CROSS
    if (currentRow.joinType === constants.JOIN_TYPE_CROSS) {
      currentRow.joinFieldId = null;
      currentRow.foreignFieldId = null;
    }

    map(function(dataSource) {
      if (currentRow === dataSource || dataSource.state === constants.OBJECT_STATE.DELETE) {
        return false;
      }

      if (
        (dataSource.joinQuerySourceId === currentRow.foreignQuerySourceId &&
          dataSource.foreignQuerySourceId === currentRow.joinQuerySourceId) ||
        (dataSource.joinQuerySourceId === currentRow.joinQuerySourceId &&
          dataSource.foreignQuerySourceId === currentRow.foreignQuerySourceId &&
          ((dataSource.joinType !== currentRow.joinType &&
            (dataSource.joinType !== constants.JOIN_TYPE.LEFT || currentRow.joinType !== constants.JOIN_TYPE.RIGHT) &&
            (dataSource.joinType !== constants.JOIN_TYPE.RIGHT || currentRow.joinType !== constants.JOIN_TYPE.LEFT)) ||
            (dataSource.joinType === constants.JOIN_TYPE.LEFT && currentRow.joinType === constants.JOIN_TYPE.LEFT) ||
            (dataSource.joinType === constants.JOIN_TYPE.RIGHT && currentRow.joinType === constants.JOIN_TYPE.RIGHT)))
      ) {
        switch (currentRow.joinType) {
          case constants.JOIN_TYPE.LEFT:
            dataSource.setJoinType(constants.JOIN_TYPE.RIGHT);
            break;
          case constants.JOIN_TYPE.RIGHT:
            dataSource.setJoinType(constants.JOIN_TYPE.LEFT);
            break;
          default:
            dataSource.setJoinType(currentRow.joinType);
        }
      }

      if (
        dataSource.joinQuerySourceId === currentRow.joinQuerySourceId &&
        dataSource.foreignQuerySourceId === currentRow.foreignQuerySourceId &&
        dataSource.joinType !== currentRow.joinType
      ) {
        dataSource.setJoinType(currentRow.joinType);
      }

      if (dataSource.joinType === constants.JOIN_TYPE_CROSS) {
        dataSource.joinFieldId = null;
        dataSource.foreignFieldId = null;
      }
    }, this.relationshipList);
  });

  /**
   * Get index of a modified relationship in modifiedRelationships
   * @param {object} relationship
   */
  RelationshipVM.prototype.getIndexOfModified = function(relationship) {
    return findIndex(item => item.position == relationship.position, this.modifiedRelationships);
  };

  /**
   * Set editable for fields in Relationship List
   * @param {boolean} isEditable
   */
  RelationshipVM.prototype.setEditable = function(isEditable) {
    this.editable = isEditable;
  };

  /**
   * Toggle Invalid Screen
   * @param {boolean} isInvalid
   */
  RelationshipVM.prototype.setShowInvalidScreen = function(isInvalid) {
    this.showInvalidScreen = isInvalid;
  };

  module.exports = RelationshipVM;
})();



// WEBPACK FOOTER //
// ./ui/logic/Izenda.RelationshipStore.js